import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { DuplicateCustomerSignal, DuplicateResolutionMode } from '@/contracts/customers'
import { ShieldCheck } from 'lucide-react'

type DuplicateResolutionPanelProps = {
  duplicateSignals: DuplicateCustomerSignal[]
  duplicateDecision?: DuplicateResolutionMode
  onDuplicateDecision?: (decision: DuplicateResolutionMode) => void
  compact?: boolean
  decisionOnly?: boolean
  remoteErpExists?: boolean
  allowCreateNewCustomer?: boolean
}

export function DuplicateResolutionPanel({
  duplicateSignals,
  duplicateDecision,
  onDuplicateDecision,
  remoteErpExists = false,
  allowCreateNewCustomer = false,
}: DuplicateResolutionPanelProps) {
  const duplicateSources = summarizeDuplicateSources(duplicateSignals)
  if (duplicateSignals.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        未偵測到客戶簡稱或統一編號的重複訊號。
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 font-semibold text-amber-950">
            <ShieldCheck className="size-4" />
            偵測到可能重複客戶
          </div>
          {onDuplicateDecision ? (
            <p className="mt-1 text-sm text-amber-800">
              {remoteErpExists
                ? 'ERP 或專案資料庫已有相近資料，請選擇這次要如何處理。'
                : '專案資料庫已有相近資料，請選擇這次要如何處理。'}
            </p>
          ) : null}
          {duplicateSources.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {duplicateSources.map((source) => (
                <Badge key={source.key} variant={source.variant}>
                  {source.label}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
        {onDuplicateDecision ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={duplicateDecision === 'merge_preview' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onDuplicateDecision('merge_preview')}
            >
              更新既有客戶
            </Button>
            {allowCreateNewCustomer ? (
              <Button
                type="button"
                variant={duplicateDecision === 'new_customer' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onDuplicateDecision('new_customer')}
              >
                建立全新客戶
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function summarizeDuplicateSources(duplicateSignals: DuplicateCustomerSignal[]) {
  const sources = new Map<
    string,
    { key: string; label: string; variant: 'outline' | 'secondary' | 'warning' }
  >()
  for (const signal of duplicateSignals) {
    const source = classifyDuplicateSource(signal)
    const key = `${source.key}:${signal.matchedCustomerCode ?? ''}`
    if (sources.has(key)) continue
    sources.set(key, {
      key,
      label: signal.matchedCustomerCode
        ? `${source.label}：${signal.matchedCustomerCode}`
        : source.label,
      variant: source.variant,
    })
  }
  return Array.from(sources.values())
}

function classifyDuplicateSource(signal: DuplicateCustomerSignal) {
  const source = signal.source ?? ''
  if (source === 'd1_project_customer') {
    return {
      key: 'd1',
      label: '專案 D1 客戶資料',
      variant: 'outline' as const,
    }
  }
  if (source.includes('erp')) {
    return {
      key: 'erp',
      label: 'ERP 資料',
      variant: 'warning' as const,
    }
  }
  if (source.includes('webhook')) {
    return {
      key: 'webhook',
      label: '客戶比對資料',
      variant: 'secondary' as const,
    }
  }
  return {
    key: 'unknown',
    label: '比對資料',
    variant: 'secondary' as const,
  }
}
